import { IonContent, IonPage } from "@ionic/react";
import React, { useEffect, useState } from "react";
import { Screens, useAnalytics } from "../AppAnalyticsProvider";
import { useHistory } from "react-router";
import CustomHelmet from "../components/ComponentsFunctional/CustomHelmet/CustomHelmet";

const CONTACT_FORM_URL = process.env.REACT_APP_CONTACT_FORM_URL
    || "https://us-central1-intense-signer-264404.cloudfunctions.net/contactForm";

const INTEREST_TYPES = [
    "Athlete Investor",
    "Angel Investor",
    "Corporate Partner",
    "Institutional Investor",
    "Other",
];

const inputClass = "w-full bg-white/[0.06] border border-white/[0.12] rounded-lg px-4 py-3 text-white text-[15px] font-['Mukta_Mahee'] placeholder:text-white/30 focus:outline-none focus:border-highlight/60 focus:ring-1 focus:ring-highlight/30 transition-colors duration-200";
const labelClass = "block text-[13px] font-bold text-white/80 tracking-[0.3px] mb-2";

type Status = "idle" | "submitting" | "success" | "error";

const InvestPage: React.FC = () => {
    const history = useHistory();
    const { trackScreen } = useAnalytics();

    useEffect(() => {
        trackScreen(Screens.INVEST);
    }, [trackScreen]);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [interestType, setInterestType] = useState("");
    const [company, setCompany] = useState("");
    const [message, setMessage] = useState("");
    const [website, setWebsite] = useState(""); // honeypot

    const [status, setStatus] = useState<Status>("idle");
    const [errorMessage, setErrorMessage] = useState("");

    const handleBack = () => {
        if (history.length > 1) history.goBack();
        else history.push("/");
    };

    const isValid = () =>
        name.trim().length > 0 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
        interestType.length > 0;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (website) return; // honeypot silently
        if (!isValid()) {
            setErrorMessage("Please fill in all required fields with valid information.");
            setStatus("error");
            return;
        }

        setStatus("submitting");
        setErrorMessage("");

        try {
            const response = await fetch(CONTACT_FORM_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    formType: "invest",
                    name: name.trim(),
                    email: email.trim(),
                    interestType,
                    company: company.trim(),
                    message: message.trim(),
                    website,
                }),
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({ error: "Submission failed" }));
                if (response.status === 429) {
                    throw new Error("You've submitted too many requests. Please try again in an hour.");
                }
                throw new Error(data.error || "Something went wrong. Please try again.");
            }

            setStatus("success");
        } catch (err) {
            setStatus("error");
            setErrorMessage(err instanceof Error ? err.message : "Something went wrong.");
        }
    };

    return (
        <IonPage>
            <CustomHelmet
                title="Invest & Partner — Ecliptic"
                description="Invest in or partner with Ecliptic, the app for park skiers and snowboarders serious about progression."
                url="https://ecliptic.day/invest"
            />
            <IonContent>
                <div className="min-h-full bg-black text-white font-['Mukta_Mahee']">
                    <nav className="sticky top-0 z-50 bg-[rgba(0,0,0,0.75)] backdrop-blur-[24px] border-b border-[rgba(88,97,172,0.18)]">
                        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="text-white/65 hover:text-white text-sm font-bold tracking-[0.4px] transition-colors duration-200"
                                aria-label="Go back"
                            >
                                ← Back
                            </button>
                            <img
                                src="/assets/logo/ECLIPTIC_NEW_WHITE_PNG_NO_PADDING.png"
                                alt="Ecliptic"
                                className="h-[26px] w-auto object-contain block"
                            />
                        </div>
                    </nav>

                    <article className="max-w-2xl mx-auto px-6 py-12">
                        <h1 className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight">
                            Invest & Partner
                        </h1>
                        <p className="text-white/65 text-[15px] leading-[1.6] mb-10">
                            We're building the platform for the next generation of action sports.
                            If you want to be part of it — whether as an athlete investor, angel, corporate partner,
                            or institution — tell us a bit about yourself.
                        </p>

                        {status === "success" ? (
                            <div className="bg-highlight/10 border border-highlight/30 rounded-lg p-6">
                                <h2 className="text-xl font-bold mb-2">Thanks — we'll be in touch.</h2>
                                <p className="text-white/70 text-[15px]">
                                    Mia will follow up personally. In the meantime, feel free to explore the rest of the site.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => history.push("/")}
                                    className="mt-4 px-6 py-2 bg-highlight text-white font-bold text-[14px] rounded-lg hover:bg-highlight/80 transition-colors"
                                >
                                    Back to Home
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                                <div>
                                    <label htmlFor="invest-name" className={labelClass}>
                                        Full Name <span className="text-highlight-danger">*</span>
                                    </label>
                                    <input
                                        id="invest-name"
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className={inputClass}
                                        required
                                        aria-required="true"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="invest-email" className={labelClass}>
                                        Email <span className="text-highlight-danger">*</span>
                                    </label>
                                    <input
                                        id="invest-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className={inputClass}
                                        required
                                        aria-required="true"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="invest-type" className={labelClass}>
                                        Interest Type <span className="text-highlight-danger">*</span>
                                    </label>
                                    <select
                                        id="invest-type"
                                        value={interestType}
                                        onChange={(e) => setInterestType(e.target.value)}
                                        className={`${inputClass} appearance-none cursor-pointer`}
                                        required
                                        aria-required="true"
                                        style={{
                                            backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23ffffff80' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
                                            backgroundRepeat: "no-repeat",
                                            backgroundPosition: "right 16px center",
                                            paddingRight: "40px",
                                        }}
                                    >
                                        <option value="" disabled>Select one...</option>
                                        {INTEREST_TYPES.map((type) => (
                                            <option key={type} value={type} className="bg-black text-white">{type}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="invest-company" className={labelClass}>
                                        Company / Organization
                                    </label>
                                    <input
                                        id="invest-company"
                                        type="text"
                                        value={company}
                                        onChange={(e) => setCompany(e.target.value)}
                                        className={inputClass}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="invest-message" className={labelClass}>
                                        Brief Message
                                    </label>
                                    <textarea
                                        id="invest-message"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="Tell us about your interest in Ecliptic..."
                                        rows={5}
                                        className={`${inputClass} resize-y min-h-[120px]`}
                                    />
                                </div>

                                {/* Honeypot — hidden from real users */}
                                <input
                                    type="text"
                                    name="website"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    className="hidden"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                />

                                {status === "error" && errorMessage && (
                                    <div className="text-highlight-danger text-[14px]" role="alert">
                                        {errorMessage}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={status === "submitting"}
                                    className="w-full sm:w-auto px-8 py-3 bg-highlight text-white font-bold text-[15px] rounded-lg tracking-[0.3px] transition-all duration-200 hover:bg-highlight/80 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {status === "submitting" ? "Submitting..." : "Submit"}
                                </button>
                            </form>
                        )}
                    </article>

                    <footer className="max-w-3xl mx-auto px-6 py-10 border-t border-white/10 mt-12">
                        <div className="flex flex-wrap gap-6 text-sm text-white/50">
                            <a href="/invest" className="hover:text-white transition-colors">Invest & Partner</a>
                            <a href="/careers" className="hover:text-white transition-colors">Careers</a>
                            <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
                            <a href="/terms" className="hover:text-white transition-colors">Terms of Service</a>
                            <a href="/eula" className="hover:text-white transition-colors">EULA</a>
                            <a href="/" className="hover:text-white transition-colors ml-auto">Home</a>
                        </div>
                    </footer>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default InvestPage;
